import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { ordersApi } from '../../services/subscriberApi';
import { magazineApi } from '../../services/magazineApi';
import { toAbsoluteStorage } from '../../services/settingsApi';
import { EmptyState, LoadingState } from '../Common/PanelState';
import { SlipPreview } from './PackingDeskSection';
import { useApp } from '../../App';

const FLARE_RED = '#c4151c';
const Icon = ({ path, className = 'h-5 w-5' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
);

const formatPKR = (n) => `Rs. ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;

function paymentScreenshotUrl(order) {
  const raw = order?.payment_screenshot;
  if (!raw || typeof raw !== 'string') return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('/storage/')) return toAbsoluteStorage(raw);
  if (raw.startsWith('storage/')) return toAbsoluteStorage(`/${raw}`);
  return toAbsoluteStorage(`/storage/${raw.replace(/^\/+/, '')}`);
}

function isPaymentProofPdf(url) {
  return /\.pdf($|\?)/i.test(String(url || ''));
}

function isPlanOrder(o) {
  return String(o?.order_kind || 'single').toLowerCase() === 'plan';
}

function deskOf(o) {
  return o?.desk || { key: '', label: o?.order_status || '', hint: '', tone: 'muted' };
}

function toneClass(tone) {
  if (tone === 'ok') return 'bg-green-100 text-green-800';
  if (tone === 'warn') return 'bg-amber-100 text-amber-800';
  if (tone === 'info') return 'bg-blue-100 text-blue-800';
  return 'bg-gray-100 text-gray-700';
}

function personName(row) {
  return row.guest_name || row.subscriber?.name || row.name || '—';
}

function personEmail(row) {
  return row.guest_email || row.subscriber?.email || row.email || '';
}

function progressOf(row) {
  return row?.plan_progress || null;
}

function issueTone(status) {
  if (status === 'shipped') return 'bg-green-100 text-green-800';
  if (status === 'skipped') return 'bg-gray-100 text-gray-600';
  return 'bg-amber-100 text-amber-800';
}

function issueWord(status) {
  if (status === 'shipped') return 'Delivered';
  if (status === 'skipped') return 'Skipped';
  return 'Pending';
}

function MonthMeter({ progress, onOpen }) {
  if (!progress || !Number(progress.months)) return null;
  const pct = Math.min(100, Math.round((Number(progress.delivered || 0) / Number(progress.months)) * 100));
  return (
    <button type="button" onClick={onOpen} className="mt-2 w-full text-left group">
      <div className="flex justify-between gap-2 text-[11px] font-semibold text-gray-600 mb-1">
        <span>{progress.label}</span>
        <span className={progress.pending ? 'text-amber-700' : 'text-emerald-700'}>{progress.pending ? `${progress.pending} pending` : 'All sent'}</span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
      </div>
      {progress.next_month ? <div className="text-[11px] text-gray-500 mt-1 group-hover:underline">Next · {progress.next_month} · view months</div> : <div className="text-[11px] text-gray-400 mt-1 group-hover:underline">View months</div>}
    </button>
  );
}

const EMPTY_SUMMARY = {
  pending_payment: 0,
  plans_to_verify: 0,
  singles_to_pack: 0,
  needs_action: 0,
  active_plans: 0,
  paid_customers: 0,
  revenue_paid: 0,
  revenue_pending: 0,
  orders_today: 0,
  singles: 0,
  plans: 0,
  plan_issues_pending: 0,
  plan_issues_sent: 0,
};

export default function OrdersSection() {
  const app = useApp();
  const [tab, setTab] = useState('work');
  const [queue, setQueue] = useState('needs_action');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    payment_status: '',
    order_status: '',
    delivery_type: '',
    date_from: '',
    date_to: '',
  });
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [people, setPeople] = useState([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peoplePage, setPeoplePage] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [peopleSearch, setPeopleSearch] = useState('');
  const [peopleStatus, setPeopleStatus] = useState('');
  const [monthsFor, setMonthsFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [startIssueId, setStartIssueId] = useState('');
  const [issues, setIssues] = useState([]);
  const [preview, setPreview] = useState(null);
  const [printingId, setPrintingId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);

  const loadSummary = async () => {
    try {
      const { data } = await ordersApi.deskSummary();
      setSummary({ ...EMPTY_SUMMARY, ...(data || {}) });
    } catch {
      /* keep last */
    }
  };

  const loadOrders = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: 50,
        search: search.trim(),
        payment_status: filters.payment_status,
        order_status: filters.order_status,
        delivery_type: filters.delivery_type,
        date_from: filters.date_from,
        date_to: filters.date_to,
      };
      if (['needs_action', 'pending_payment', 'pack_singles', 'single', 'plan', 'plans_in_progress'].includes(queue)) params.queue = queue;
      Object.keys(params).forEach((k) => {
        if (!params[k] || params[k] === '') delete params[k];
      });
      const { data } = await ordersApi.getAll(params);
      setOrders(data.data || []);
      setPagination({ current_page: data.current_page, last_page: data.last_page, total: data.total });
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const loadPeople = async (page = 1) => {
    setPeopleLoading(true);
    try {
      const params = { page, per_page: 50, search: peopleSearch.trim(), status: peopleStatus };
      if (!params.search) delete params.search;
      if (!params.status) delete params.status;
      const { data } = await ordersApi.paidCustomers(params);
      setPeople(data.data || []);
      setPeoplePage({ current_page: data.current_page, last_page: data.last_page, total: data.total });
    } catch {
      toast.error('Failed to load customers');
    } finally {
      setPeopleLoading(false);
    }
  };

  useEffect(() => { loadSummary(); }, []);
  useEffect(() => {
    const t = setTimeout(() => loadOrders(1), search ? 280 : 0);
    return () => clearTimeout(t);
  }, [queue, search, filters]);
  useEffect(() => {
    if (tab !== 'people') return undefined;
    const t = setTimeout(() => loadPeople(1), peopleSearch ? 280 : 0);
    return () => clearTimeout(t);
  }, [tab, peopleSearch, peopleStatus]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await magazineApi.getIssues({ per_page: 100 });
        const list = data?.data || (Array.isArray(data) ? data : []);
        setIssues(Array.isArray(list) ? list : []);
      } catch {
        setIssues([]);
      }
    })();
  }, []);

  const refreshAll = () => {
    loadSummary();
    if (tab === 'people') loadPeople(peoplePage.current_page);
    else loadOrders(pagination.current_page);
  };

  const openAction = (order, action) => {
    setSelected({ ...order, _action: action });
    setNoteText(order.notes || '');
    setTrackingNumber(order.tracking_number || '');
    setStartIssueId(String(order.start_magazine_issue_id || order.magazine_issue_id || ''));
    setShowModal(true);
  };

  const handlePrint = async (order) => {
    if (printingId != null) return;
    setPrintingId(order.id);
    try {
      const { data } = await ordersApi.printSlips(order.id);
      const slips = data?.slips || [];
      if (!slips.length) {
        toast.error(data?.message || 'No slips to print');
        return;
      }
      const courier = data?.courier;
      if (courier?.ok && courier?.tracking_number) {
        toast.success(courier.message || `Leopards CN ${courier.tracking_number}`);
        refreshAll();
      } else if (courier && courier.ok === false && (courier.message || courier.error)) {
        toast.warn(courier.message || courier.error);
      }
      setPreview(slips);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not build print preview');
    } finally {
      setPrintingId(null);
    }
  };

  const handleEmail = async (order) => {
    try {
      const { data } = await ordersApi.sendDispatchEmail(order.id);
      toast.success(data?.message || 'Dispatch email sent');
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not send dispatch email');
    }
  };

  const handleSyncTracking = async (order) => {
    if (syncingId != null) return;
    setSyncingId(order.id);
    try {
      const { data } = await ordersApi.syncTracking(order.id);
      if (data?.auto_delivered) {
        toast.success(data.message || 'Delivered on Leopards — order closed');
      } else if (data?.ok) {
        toast.success(data.message || 'Tracking synced');
      } else {
        toast.warn(data?.error || data?.message || 'No status yet');
      }
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.error || e.response?.data?.message || 'Could not sync Leopards tracking');
    } finally {
      setSyncingId(null);
    }
  };

  const runPrimary = (order) => {
    const key = deskOf(order).key;
    if (key === 'mark_paid') openAction(order, 'markPaid');
    else if (key === 'print') handlePrint(order);
    else if (key === 'ship') openAction(order, 'ship');
    else if (key === 'email') handleEmail(order);
    else if (key === 'deliver') openAction(order, 'markDelivered');
    else if (key === 'packing') window.location.assign('/magazine/packing');
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const { _action } = selected;
      const payload = {};
      if (noteText) payload.notes = noteText;
      if (_action === 'markDelivered') {
        if (trackingNumber) payload.tracking_number = trackingNumber;
        await ordersApi.markDelivered(selected.id, payload);
        toast.success('Order marked as delivered');
      } else if (_action === 'markPaid') {
        if (isPlanOrder(selected) && startIssueId) payload.start_magazine_issue_id = Number(startIssueId);
        await ordersApi.markPaid(selected.id, payload);
        toast.success(isPlanOrder(selected) ? 'Plan verified — subscriber is Active Paid' : 'Payment verified');
      } else if (_action === 'ship') {
        if (trackingNumber) payload.tracking_number = trackingNumber;
        await ordersApi.ship(selected.id, payload);
        toast.success('Order marked shipped');
      } else if (_action === 'cancel') {
        await ordersApi.cancel(selected.id, payload);
        toast.success('Order cancelled');
      }
      setShowModal(false);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const openHistory = async (person) => {
    setHistoryFor(person);
    try {
      const email = personEmail(person);
      const { data } = await ordersApi.getAll({ search: email || person.name, per_page: 50 });
      const rows = data.data || [];
      const mine = rows.filter((o) => {
        const em = personEmail(o).toLowerCase();
        return (email && em === String(email).toLowerCase()) || Number(o.subscriber_id) === Number(person.id);
      });
      setHistory(mine.length ? mine : rows);
    } catch {
      setHistory([]);
    }
  };

  const exportExcel = async () => {
    try {
      const params = {
        per_page: 500,
        search: search.trim(),
        payment_status: filters.payment_status,
        order_status: filters.order_status,
        delivery_type: filters.delivery_type,
        date_from: filters.date_from,
        date_to: filters.date_to,
      };
      if (['needs_action', 'pending_payment', 'pack_singles', 'single', 'plan', 'plans_in_progress'].includes(queue)) params.queue = queue;
      Object.keys(params).forEach((k) => {
        if (!params[k] || params[k] === '') delete params[k];
      });
      const { data } = await ordersApi.getAll(params);
      const rows = data.data || [];
      const header = ['Order Number', 'Kind', 'Name', 'Email', 'Phone', 'City', 'Item', 'Qty', 'Total', 'Payment', 'Status', 'Next', 'Created'];
      const csv = [header.join(',')].concat(rows.map((o) => [
        o.order_number,
        isPlanOrder(o) ? 'Plan' : 'Single',
        personName(o),
        personEmail(o),
        o.guest_phone || o.subscriber?.phone || '',
        o.guest_city || o.subscriber?.city || '',
        isPlanOrder(o) ? (o.subscription_plan?.name || 'Plan') : (o.magazine_issue?.title || ''),
        o.quantity,
        o.total_amount_pkr,
        o.payment_status,
        o.order_status,
        deskOf(o).label,
        o.created_at,
      ].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))).join('\n');
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `website-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} order(s)`);
    } catch {
      toast.error('Export failed');
    }
  };

  const chips = [
    { id: 'needs_action', label: `Needs action (${summary.needs_action})` },
    { id: 'all', label: 'All orders' },
    { id: 'single', label: `Single (${summary.singles})` },
    { id: 'plan', label: `Plan (${summary.plans})` },
    { id: 'plans_in_progress', label: `Plan copies pending (${summary.plan_issues_pending})` },
  ];

  const chipOn = (id) => {
    if (id === 'needs_action') return queue === 'needs_action' || queue === 'pending_payment';
    if (id === 'single') return queue === 'single' || queue === 'pack_singles';
    if (id === 'plan') return queue === 'plan';
    return queue === id;
  };

  const openKpi = (id) => {
    setFilters({ payment_status: '', order_status: '', delivery_type: '', date_from: '', date_to: '' });
    setSearch('');
    if (id === 'verify') {
      setTab('work');
      setQueue('pending_payment');
    } else if (id === 'pack') {
      setTab('work');
      setQueue('pack_singles');
    } else if (id === 'plans') {
      setTab('people');
      setPeopleStatus('Active');
    } else if (id === 'people') {
      setTab('people');
      setPeopleStatus('');
    } else {
      setTab('work');
      setQueue('all');
    }
  };

  const kpis = [
    {
      id: 'verify',
      label: 'Verify payment',
      value: summary.pending_payment,
      hint: formatPKR(summary.revenue_pending),
      color: '#d97706',
      path: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    },
    {
      id: 'pack',
      label: 'Singles to pack',
      value: summary.singles_to_pack,
      hint: 'Print on this page',
      color: '#1d4ed8',
      path: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
    },
    {
      id: 'plans',
      label: 'Active plans',
      value: summary.active_plans,
      hint: `${summary.plan_issues_sent} sent · ${summary.plan_issues_pending} pending`,
      color: FLARE_RED,
      path: 'M5 5a2 2 0 012-2h10a2 2 0 012 2v1h2a1 1 0 011 1v3a1 1 0 01-1 1h-2v1a2 2 0 01-2 2H7a2 2 0 01-2-2v-1H3a1 1 0 01-1-1V7a1 1 0 011-1h2V5z',
    },
    {
      id: 'people',
      label: 'Paid customers',
      value: summary.paid_customers,
      hint: 'One row per person',
      color: '#0f766e',
      path: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
    },
    {
      id: 'paid',
      label: 'Paid in',
      value: formatPKR(summary.revenue_paid),
      hint: `${summary.orders_today} orders today`,
      color: '#15803d',
      path: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="admin-page-title">Website Orders</h1>
          <p className="admin-page-sub">
            Single magazines and plans in one place. Complimentary stays on{' '}
            <Link to="/magazine/subscribers" className="font-semibold underline" style={{ color: FLARE_RED }}>Subscribers</Link>
            . Duplicate checkouts reuse the same order number.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={exportExcel} className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg shadow hover:bg-emerald-700 text-sm font-semibold">
            <Icon path="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" className="h-4 w-4" />
            Export Excel
          </button>
          <button type="button" onClick={refreshAll} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-800 rounded-lg shadow-sm hover:bg-gray-50 text-sm font-semibold">
            <Icon path="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {kpis.map((k) => (
          <button
            key={k.id}
            type="button"
            onClick={() => openKpi(k.id)}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 text-left hover:border-gray-200 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: k.color }}>
                <Icon path={k.path} className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{k.label}</div>
                <div className="text-2xl font-extrabold text-gray-900 mt-0.5 truncate">{k.value}</div>
                <div className="text-xs text-gray-500 mt-0.5 truncate">{k.hint}</div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        {[
          { id: 'work', label: 'Work queue' },
          { id: 'people', label: 'Customers' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-bold ${tab === t.id ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'work' && (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
            <div className="flex flex-wrap gap-2">
              {chips.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setQueue(c.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border ${chipOn(c.id) ? 'text-white border-transparent' : 'bg-white text-gray-700 border-gray-200'}`}
                  style={chipOn(c.id) ? { backgroundColor: FLARE_RED } : undefined}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-600">Payment Status</label>
                <select className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 focus:border-transparent" value={filters.payment_status} onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })}>
                  <option value="">All</option>
                  <option>Pending</option>
                  <option>Paid</option>
                  <option>Rejected</option>
                  <option>Refunded</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Order Status</label>
                <select className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 focus:border-transparent" value={filters.order_status} onChange={(e) => setFilters({ ...filters, order_status: e.target.value })}>
                  <option value="">All</option>
                  <option>Pending</option>
                  <option>Processing</option>
                  <option>Shipped</option>
                  <option>Delivered</option>
                  <option>Cancelled</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Delivery</label>
                <select className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 focus:border-transparent" value={filters.delivery_type} onChange={(e) => setFilters({ ...filters, delivery_type: e.target.value })}>
                  <option value="">All</option>
                  <option>Digital</option>
                  <option>Physical</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">From</label>
                <input type="date" className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 focus:border-transparent" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">To</label>
                <input type="date" className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 focus:border-transparent" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Search</label>
                <input type="text" placeholder="Order #, name, email..." className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 focus:border-transparent" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {loading ? (
              <LoadingState label="Loading paid orders" />
            ) : orders.length === 0 ? (
              <EmptyState
                title={queue === 'pack_singles' ? 'No singles to pack' : (queue === 'needs_action' || queue === 'pending_payment' ? 'Nothing waiting' : 'No orders')}
                hint={queue === 'pack_singles'
                  ? 'After Mark Paid, physical single copies wait here for print and ship.'
                  : (queue === 'needs_action' || queue === 'pending_payment'
                    ? 'New website checkouts appear here until you verify payment or pack the single copy.'
                    : 'Shop checkouts show here automatically.')}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left">Order</th>
                      <th className="px-4 py-3 text-left">Customer</th>
                      <th className="px-4 py-3 text-left">What they bought</th>
                      <th className="px-4 py-3 text-left">Amount</th>
                      <th className="px-4 py-3 text-left">Tracking</th>
                      <th className="px-4 py-3 text-left">Next step</th>
                      <th className="px-4 py-3 text-left">Do this</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {orders.map((o) => {
                      const desk = deskOf(o);
                      const track = String(o.tracking_number || '').trim();
                      const payLabel = o.payment_method === 'Cash' ? 'COD' : (o.payment_method || '');
                      return (
                        <tr key={o.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-900">{o.order_number}</div>
                            <div className="text-xs text-gray-500">{new Date(o.created_at).toLocaleDateString()}</div>
                            <span className={`mt-1 inline-block px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${isPlanOrder(o) ? 'bg-rose-50 text-rose-800' : 'bg-slate-100 text-slate-700'}`}>
                              {isPlanOrder(o) ? 'Plan' : 'Single'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{personName(o)}</div>
                            <div className="text-xs text-gray-500">{personEmail(o) || '—'}</div>
                            <div className="text-xs text-gray-500">{o.guest_phone || o.subscriber?.phone || ''}{o.guest_city ? ` · ${o.guest_city}` : ''}</div>
                          </td>
                          <td className="px-4 py-3">
                            {isPlanOrder(o) ? (
                              <>
                                <div className="font-medium">{o.subscription_plan?.name || 'Plan'}</div>
                                <div className="text-xs text-gray-500">From {(o.start_magazine_issue || o.magazine_issue)?.month_year || ''}{o.subscription_plan?.duration_months ? ` · ${o.subscription_plan.duration_months} months` : ''}</div>
                                <MonthMeter progress={progressOf(o)} onOpen={() => setMonthsFor({ title: `${personName(o)} · ${o.subscription_plan?.name || 'Plan'}`, progress: progressOf(o) })} />
                              </>
                            ) : (
                              <>
                                <div className="font-medium">{o.magazine_issue?.title || `Issue #${o.magazine_issue_id}`}</div>
                                <div className="text-xs text-gray-500">{o.delivery_type} · qty {o.quantity}</div>
                              </>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold">{formatPKR(o.total_amount_pkr)}</div>
                            {payLabel ? <div className="text-[11px] text-gray-500 mt-0.5">{payLabel} · {o.payment_status}</div> : <div className="text-[11px] text-gray-500 mt-0.5">{o.payment_status}</div>}
                            {paymentScreenshotUrl(o) ? (
                              <a
                                href={paymentScreenshotUrl(o)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block mt-1 text-[11px] font-bold text-indigo-700 hover:underline"
                              >
                                View proof →
                              </a>
                            ) : null}
                          </td>
                          <td className="px-4 py-3">
                            {track ? (
                              <div>
                                <div className="font-mono text-xs font-bold text-gray-900 tracking-wide">{track}</div>
                                <div className="text-[10px] font-semibold uppercase text-emerald-700 mt-0.5">Leopards</div>
                                {o.courier_status ? (
                                  <div className="text-[11px] text-gray-600 mt-1 max-w-[9rem] leading-snug">{o.courier_status}</div>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">Not booked</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${toneClass(desk.tone)}`}>{desk.label}</span>
                            {desk.hint ? <div className="text-xs text-gray-500 mt-1 max-w-[11rem]">{desk.hint}</div> : null}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1.5 items-center">
                              {desk.key === 'mark_paid' && (
                                <button type="button" onClick={() => openAction(o, 'markPaid')} className="px-3 py-1.5 bg-green-600 text-white text-xs font-bold rounded-lg hover:bg-green-700">
                                  Verify payment
                                </button>
                              )}
                              {desk.key === 'print' && (
                                <button type="button" onClick={() => handlePrint(o)} disabled={printingId === o.id} className="px-3 py-1.5 bg-slate-800 text-white text-xs font-bold rounded-lg disabled:opacity-50">
                                  {printingId === o.id ? 'Booking CN…' : 'Print slip'}
                                </button>
                              )}
                              {desk.key === 'ship' && (
                                <>
                                  <button type="button" onClick={() => openAction(o, 'ship')} className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-lg hover:bg-indigo-700">
                                    Mark shipped
                                  </button>
                                  <button type="button" onClick={() => handlePrint(o)} disabled={printingId === o.id} className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-bold rounded-lg disabled:opacity-50" title="Reprint label — does not book a new CN">
                                    {printingId === o.id ? '…' : 'Reprint'}
                                  </button>
                                </>
                              )}
                              {desk.key === 'email' && (
                                <>
                                  <button type="button" onClick={() => handleEmail(o)} className="px-3 py-1.5 bg-amber-600 text-white text-xs font-bold rounded-lg hover:bg-amber-700">
                                    Email tracking
                                  </button>
                                  {track ? (
                                    <button type="button" onClick={() => handleSyncTracking(o)} disabled={syncingId === o.id} className="px-3 py-1.5 bg-white border border-gray-200 text-gray-800 text-xs font-bold rounded-lg disabled:opacity-50">
                                      {syncingId === o.id ? 'Syncing…' : 'Sync tracking'}
                                    </button>
                                  ) : null}
                                </>
                              )}
                              {desk.key === 'deliver' && (
                                <>
                                  <button type="button" onClick={() => handleSyncTracking(o)} disabled={syncingId === o.id} className="px-3 py-1.5 bg-teal-700 text-white text-xs font-bold rounded-lg hover:bg-teal-800 disabled:opacity-50">
                                    {syncingId === o.id ? 'Syncing…' : 'Sync tracking'}
                                  </button>
                                  <button type="button" onClick={() => openAction(o, 'markDelivered')} className="px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700">
                                    Mark delivered
                                  </button>
                                </>
                              )}
                              {desk.key === 'packing' && (
                                <>
                                  <button type="button" onClick={() => setMonthsFor({ title: `${personName(o)} · ${o.subscription_plan?.name || 'Plan'}`, progress: progressOf(o) })} className="px-3 py-1.5 bg-white border border-gray-200 text-gray-800 text-xs font-bold rounded-lg">Months</button>
                                  <Link to="/magazine/packing" className="px-3 py-1.5 bg-white border border-rose-200 text-rose-800 text-xs font-bold rounded-lg">Open Packing Desk</Link>
                                </>
                              )}
                              {desk.key === 'done' && (
                                <span className="text-xs font-semibold text-emerald-700">Complete</span>
                              )}
                              {desk.key !== 'cancelled' && desk.key !== 'done' && (
                                <button type="button" onClick={() => openAction(o, 'cancel')} className="px-2 py-1.5 text-xs font-semibold text-red-600 hover:underline">Cancel</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {pagination.last_page > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-gray-100 bg-gray-50">
                <div className="text-sm text-gray-600">{pagination.total} · page {pagination.current_page}/{pagination.last_page}</div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => loadOrders(pagination.current_page - 1)} disabled={pagination.current_page <= 1} className="px-3 py-1 bg-white border rounded text-sm disabled:opacity-50">Prev</button>
                  <button type="button" onClick={() => loadOrders(pagination.current_page + 1)} disabled={pagination.current_page >= pagination.last_page} className="px-3 py-1 bg-white border rounded text-sm disabled:opacity-50">Next</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'people' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3 border-b border-gray-100">
            <input type="search" value={peopleSearch} onChange={(e) => setPeopleSearch(e.target.value)} placeholder="Name, email, phone…" className="text-sm border border-gray-300 rounded-lg p-2.5 sm:col-span-2" />
            <select value={peopleStatus} onChange={(e) => setPeopleStatus(e.target.value)} className="text-sm border border-gray-300 rounded-lg p-2.5">
              <option value="">All statuses</option>
              <option value="Active">Active plan</option>
              <option value="Inactive">Inactive / single</option>
            </select>
          </div>
          {peopleLoading ? (
            <LoadingState label="Loading paid customers" />
          ) : people.length === 0 ? (
            <EmptyState title="No paid customers yet" hint="After you verify a website order, that person appears here — one row, all their orders." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-700 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left">Customer</th>
                    <th className="px-4 py-3 text-left">Plan / single</th>
                    <th className="px-4 py-3 text-left">Months</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Orders</th>
                    <th className="px-4 py-3 text-left" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {people.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-gray-500">{p.email}</div>
                        <div className="text-xs text-gray-500">{p.phone}{p.city ? ` · ${p.city}` : ''}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{p.plan_label || '—'}</div>
                        {p.start_magazine_issue?.month_year ? <div className="text-xs text-gray-500">From {p.start_magazine_issue.month_year}</div> : null}
                      </td>
                      <td className="px-4 py-3 min-w-[160px]">
                        {progressOf(p)?.months ? (
                          <MonthMeter progress={progressOf(p)} onOpen={() => setMonthsFor({ title: `${p.name} · ${p.plan_label || 'Plan'}`, progress: progressOf(p) })} />
                        ) : (
                          <span className="text-xs text-gray-500">Single issue</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${p.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>{p.status}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{p.orders_count || 0}</div>
                        {p.open_orders > 0 ? <div className="text-xs text-amber-700">{p.open_orders} need action</div> : <div className="text-xs text-gray-500">Clear</div>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button type="button" onClick={() => openHistory(p)} className="px-3 py-1.5 text-xs font-bold rounded-lg border border-gray-200 hover:bg-gray-50">Orders</button>
                        {p.status === 'Active' ? <Link to="/magazine/packing" className="ml-1 px-3 py-1.5 text-xs font-bold rounded-lg text-rose-800 border border-rose-200">Pack</Link> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {peoplePage.last_page > 1 && (
            <div className="flex justify-end gap-2 p-4 border-t">
              <button type="button" onClick={() => loadPeople(peoplePage.current_page - 1)} disabled={peoplePage.current_page <= 1} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Prev</button>
              <button type="button" onClick={() => loadPeople(peoplePage.current_page + 1)} disabled={peoplePage.current_page >= peoplePage.last_page} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Next</button>
            </div>
          )}
        </div>
      )}

      {historyFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setHistoryFor(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b flex justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold">{historyFor.name}</h3>
                <p className="text-sm text-gray-500">{historyFor.email} · {historyFor.plan_label}</p>
                {progressOf(historyFor)?.months ? (
                  <div className="mt-2 max-w-sm">
                    <MonthMeter progress={progressOf(historyFor)} onOpen={() => setMonthsFor({ title: `${historyFor.name} · ${historyFor.plan_label || 'Plan'}`, progress: progressOf(historyFor) })} />
                  </div>
                ) : null}
              </div>
              <button type="button" onClick={() => setHistoryFor(null)} className="text-sm font-semibold text-gray-500">Close</button>
            </div>
            <div className="divide-y">
              {history.length === 0 ? <p className="p-5 text-sm text-gray-500">No website orders linked yet.</p> : history.map((o) => (
                <div key={o.id} className="p-4 flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold">{o.order_number}</div>
                    <div className="text-xs text-gray-500">{isPlanOrder(o) ? (o.subscription_plan?.name || 'Plan') : (o.magazine_issue?.title || 'Single')} · {formatPKR(o.total_amount_pkr)}</div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${toneClass(deskOf(o).tone)}`}>{deskOf(o).label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {monthsFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setMonthsFor(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b flex justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold">Plan months</h3>
                <p className="text-sm text-gray-500">{monthsFor.title}</p>
                <p className="text-sm font-semibold text-gray-800 mt-1">{monthsFor.progress?.label} · {monthsFor.progress?.pending || 0} pending</p>
              </div>
              <button type="button" onClick={() => setMonthsFor(null)} className="text-sm font-semibold text-gray-500">Close</button>
            </div>
            <div className="p-3 space-y-1">
              {(monthsFor.progress?.issues || []).length === 0 ? (
                <p className="p-3 text-sm text-gray-500">{monthsFor.progress?.hint || 'Months appear after you verify the plan payment.'}</p>
              ) : monthsFor.progress.issues.map((issue, idx) => (
                <div key={issue.id || idx} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{issue.month}</div>
                    <div className="text-[11px] text-gray-500">Month {idx + 1} of {monthsFor.progress.months}</div>
                  </div>
                  <span className={`px-2 py-1 text-[11px] font-bold rounded-full ${issueTone(issue.status)}`}>{issueWord(issue.status)}</span>
                </div>
              ))}
            </div>
            {monthsFor.progress?.pending > 0 ? (
              <div className="p-4 border-t">
                <Link to="/magazine/packing" className="block text-center px-4 py-2.5 rounded-lg text-white text-sm font-bold" style={{ backgroundColor: FLARE_RED }}>Open Packing Desk</Link>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {showModal && selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-5 border-b border-gray-100">
              <h3 className="text-lg font-bold">
                {selected._action === 'markPaid' && (isPlanOrder(selected) ? 'Verify plan payment' : 'Verify payment')}
                {selected._action === 'markDelivered' && 'Mark delivered'}
                {selected._action === 'ship' && 'Mark shipped'}
                {selected._action === 'cancel' && 'Cancel order'}
              </h3>
              <p className="text-sm text-gray-500">{selected.order_number} · {personName(selected)}</p>
            </div>
            <div className="p-5 space-y-4">
              {selected._action === 'markPaid' && (
                <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-3 space-y-2">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-indigo-800">Bank transfer proof</div>
                  {selected.transaction_id ? (
                    <div className="text-sm text-gray-800">
                      <span className="font-semibold text-gray-600">Reference:</span>{' '}
                      <span className="font-mono font-bold">{selected.transaction_id}</span>
                    </div>
                  ) : (
                    <div className="text-xs text-amber-800">No transaction ID entered by customer.</div>
                  )}
                  {paymentScreenshotUrl(selected) ? (
                    isPaymentProofPdf(paymentScreenshotUrl(selected)) ? (
                      <a
                        href={paymentScreenshotUrl(selected)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm font-bold text-indigo-700 hover:underline"
                      >
                        Open payment PDF →
                      </a>
                    ) : (
                      <a href={paymentScreenshotUrl(selected)} target="_blank" rel="noopener noreferrer" className="block">
                        <img
                          src={paymentScreenshotUrl(selected)}
                          alt="Payment screenshot"
                          className="max-h-56 w-full object-contain rounded-lg border border-indigo-100 bg-white"
                        />
                        <div className="text-[11px] font-semibold text-indigo-700 mt-1.5">Open full size →</div>
                      </a>
                    )
                  ) : (
                    <div className="text-xs text-amber-800">No payment screenshot uploaded.</div>
                  )}
                </div>
              )}
              {selected._action === 'markPaid' && isPlanOrder(selected) && (
                <div>
                  <label className="text-sm font-semibold text-gray-700">First magazine month</label>
                  <select className="w-full mt-1 border border-gray-300 rounded-lg p-2 text-sm" value={startIssueId} onChange={(e) => setStartIssueId(e.target.value)}>
                    <option value="">Select start month</option>
                    {issues.map((issue) => (
                      <option key={issue.id} value={issue.id}>{issue.month_year || issue.title || `Issue #${issue.id}`}</option>
                    ))}
                  </select>
                </div>
              )}
              {selected._action === 'ship' && trackingNumber && (
                <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2.5">
                  <div className="text-[11px] font-bold uppercase text-emerald-800 tracking-wide">Leopards CN</div>
                  <div className="font-mono text-sm font-bold text-gray-900 mt-0.5">{trackingNumber}</div>
                  <div className="text-xs text-emerald-800/80 mt-1">Already booked — marking shipped will not create a new CN.</div>
                </div>
              )}
              {selected._action === 'ship' && !trackingNumber && (
                <div>
                  <label className="text-sm font-semibold text-gray-700">Tracking (optional)</label>
                  <input className="w-full mt-1 border border-gray-300 rounded-lg p-2 text-sm" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} placeholder="Enter CN if booked outside the app" />
                  <p className="text-xs text-amber-700 mt-1">No CN yet — prefer Print slip first so Leopards books automatically.</p>
                </div>
              )}
              {selected._action === 'markDelivered' && (
                <div>
                  <label className="text-sm font-semibold text-gray-700">Tracking</label>
                  <input className="w-full mt-1 border border-gray-300 rounded-lg p-2 text-sm font-mono" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} placeholder="Courier tracking number" />
                </div>
              )}
              <div>
                <label className="text-sm font-semibold text-gray-700">Notes (optional)</label>
                <textarea rows="3" className="w-full mt-1 border border-gray-300 rounded-lg p-2 text-sm" value={noteText} onChange={(e) => setNoteText(e.target.value)} />
              </div>
            </div>
            <div className="p-5 border-t border-gray-100 flex justify-end gap-2">
              <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg text-sm font-semibold" disabled={submitting}>Back</button>
              <button type="button" onClick={handleConfirm} className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${selected._action === 'cancel' ? 'bg-red-600' : 'bg-green-600'}`} disabled={submitting}>
                {submitting ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      <SlipPreview slips={preview} branding={app} onClose={() => setPreview(null)} onPrint={() => window.print()} />
    </div>
  );
}
